import React, { useState, useEffect } from 'react';
import { Button, Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, Input, Select, SelectItem, Textarea, Chip } from '@nextui-org/react';
import { FiPlus, FiEdit2, FiTrash2, FiDollarSign, FiDownload, FiFilter } from 'react-icons/fi';
import { BsBank, BsBox, BsCart3, BsCashStack, BsCheckCircle, BsXCircle, BsClock } from 'react-icons/bs';
import Notiflix from 'notiflix';
import { 
  getProjectFinancials, 
  addProjectFinancial, 
  updateProjectFinancial, 
  deleteProjectFinancial,
  getProjectFinancialSummary,
  exportProjectFinancialsCSV 
} from '../../../services/financials';

const FinancialComponent = ({ projectId }) => {
  const [financials, setFinancials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [filterType, setFilterType] = useState('all');

  // Form state
  const [formData, setFormData] = useState({
    type: 'expense', // expense, income, asset
    category: '',
    description: '',
    amount: '',
    date: new Date().toISOString().split('T')[0],
    notes: '',
    payment_status: 'unpaid', // paid, unpaid, partial
    payment_method: '', // cash, bank_transfer, credit_card, other
    invoice_number: '',
    due_date: '',
    currency: 'TRY', // TRY, USD, EUR
    exchange_rate: '1.0000',
    tags: []
  });

  const [summary, setSummary] = useState(null);
  const [tagInput, setTagInput] = useState('');

  // Kategoriler
  const categories = {
    expense: [
      { value: 'demirbas', label: 'Demirbaş Alımı' },
      { value: 'malzeme', label: 'Malzeme' },
      { value: 'hizmet', label: 'Hizmet Alımı' },
      { value: 'personel', label: 'Personel Gideri' },
      { value: 'kira', label: 'Kira' },
      { value: 'fatura', label: 'Fatura Ödemesi' },
      { value: 'diger_gider', label: 'Diğer Gider' }
    ],
    income: [
      { value: 'satis', label: 'Ürün Satışı' },
      { value: 'hizmet_geliri', label: 'Hizmet Geliri' },
      { value: 'fatura_tahsilat', label: 'Fatura Tahsilatı' },
      { value: 'diger_gelir', label: 'Diğer Gelir' }
    ],
    asset: [
      { value: 'makine', label: 'Makine' },
      { value: 'arac', label: 'Araç' },
      { value: 'ekipman', label: 'Ekipman' },
      { value: 'mobilya', label: 'Mobilya' },
      { value: 'diger_demirbas', label: 'Diğer Demirbaş' }
    ]
  };

  useEffect(() => {
    if (projectId) {
      fetchFinancials();
    }
  }, [projectId]);

  const fetchFinancials = async () => {
    try {
      setLoading(true);
      const response = await getProjectFinancials({ slug: projectId });
      setFinancials(response.data || []);
      
      // Özet bilgilerini de çek
      try {
        const summaryData = await getProjectFinancialSummary({ slug: projectId });
        setSummary(summaryData);
      } catch (summaryError) {
      }
    } catch (error) {
      Notiflix.Notify.failure('Finansal kayıtlar yüklenirken hata oluştu!');
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = async () => {
    try {
      Notiflix.Loading.pulse('CSV dosyası hazırlanıyor...');
      await exportProjectFinancialsCSV({ slug: projectId });
      Notiflix.Loading.remove();
      Notiflix.Notify.success('CSV dosyası indirildi!');
    } catch (error) {
      Notiflix.Loading.remove();
      Notiflix.Notify.failure('CSV indirme sırasında hata oluştu!');
    }
  };

  const handleSubmit = async () => {
    // Validasyon
    if (!formData.category || !formData.description || !formData.amount || !formData.date) {
      Notiflix.Notify.warning('Lütfen tüm zorunlu alanları doldurun!');
      return;
    }

    try {
      Notiflix.Loading.pulse('Kaydediliyor...');

      if (editingItem) {
        // Güncelleme
        await updateProjectFinancial({
          slug: projectId,
          id: editingItem.id,
          ...formData
        });
        Notiflix.Notify.success('Kayıt başarıyla güncellendi!');
      } else {
        // Yeni ekleme
        await addProjectFinancial({
          slug: projectId,
          ...formData
        });
        Notiflix.Notify.success('Kayıt başarıyla eklendi!');
      }

      Notiflix.Loading.remove();
      setIsModalOpen(false);
      resetForm();
      fetchFinancials();
    } catch (error) {
      Notiflix.Loading.remove();
      const errorMessage = error?.response?.data?.message || 'İşlem sırasında hata oluştu!';
      Notiflix.Notify.failure(errorMessage);
    }
  };

  const handleDelete = async (item) => {
    Notiflix.Confirm.show(
      'Kaydı Sil',
      `"${item.description}" kaydını silmek istediğinize emin misiniz?`,
      'Evet',
      'Hayır',
      async () => {
        try {
          Notiflix.Loading.pulse('Siliniyor...');
          await deleteProjectFinancial({
            slug: projectId,
            id: item.id
          });
          Notiflix.Loading.remove();
          Notiflix.Notify.success('Kayıt başarıyla silindi!');
          fetchFinancials();
        } catch (error) {
          Notiflix.Loading.remove();
          const errorMessage = error?.response?.data?.message || 'Silme işlemi sırasında hata oluştu!';
          Notiflix.Notify.failure(errorMessage);
        }
      }
    );
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      type: item.type,
      category: item.category,
      description: item.description,
      amount: item.amount,
      date: item.date,
      notes: item.notes || '',
      payment_status: item.payment_status || 'unpaid',
      payment_method: item.payment_method || '',
      invoice_number: item.invoice_number || '',
      due_date: item.due_date || '',
      currency: item.currency || 'TRY',
      exchange_rate: item.exchange_rate || '1.0000',
      tags: item.tags || []
    });
    setIsModalOpen(true);
  };

  const resetForm = () => {
    setEditingItem(null);
    setFormData({
      type: 'expense',
      category: '',
      description: '',
      amount: '',
      date: new Date().toISOString().split('T')[0],
      notes: '',
      payment_status: 'unpaid',
      payment_method: '',
      invoice_number: '',
      due_date: '',
      currency: 'TRY',
      exchange_rate: '1.0000',
      tags: []
    });
    setTagInput('');
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData({ ...formData, tags: [...formData.tags, tagInput.trim()] });
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setFormData({ ...formData, tags: formData.tags.filter(tag => tag !== tagToRemove) });
  };

  const getPaymentStatusIcon = (status) => {
    switch (status) {
      case 'paid':
        return <BsCheckCircle className="text-green-600" />;
      case 'unpaid':
        return <BsXCircle className="text-red-600" />;
      case 'partial':
        return <BsClock className="text-yellow-600" />;
      default:
        return null;
    }
  };

  const getPaymentStatusLabel = (status) => {
    switch (status) {
      case 'paid':
        return 'Ödendi';
      case 'unpaid':
        return 'Ödenmedi';
      case 'partial':
        return 'Kısmi';
      default:
        return status;
    }
  };

  const getPaymentMethodLabel = (method) => {
    switch (method) {
      case 'cash':
        return 'Nakit';
      case 'bank_transfer':
        return 'Banka Transferi';
      case 'credit_card':
        return 'Kredi Kartı';
      case 'other':
        return 'Diğer';
      default:
        return method;
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'income':
        return <BsCashStack className="text-green-600" />;
      case 'expense':
        return <FiDollarSign className="text-red-600" />;
      case 'asset':
        return <BsBox className="text-blue-600" />;
      default:
        return <BsBank />;
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'income':
        return 'Gelir';
      case 'expense':
        return 'Gider';
      case 'asset':
        return 'Demirbaş';
      default:
        return type;
    }
  };

  const getCategoryLabel = (type, category) => {
    const categoryList = categories[type] || [];
    const found = categoryList.find(c => c.value === category);
    return found ? found.label : category;
  };

  const filteredFinancials = financials.filter(item => {
    if (filterType === 'all') return true;
    return item.type === filterType;
  });

  // Toplam hesaplama - Backend'den gelen özet varsa onu kullan, yoksa frontend'de hesapla
  const totals = summary ? {
    income: summary.total_income || 0,
    expense: summary.total_expense || 0,
    asset: summary.total_asset || 0
  } : financials.reduce((acc, item) => {
    if (item.type === 'income') {
      acc.income += parseFloat(item.amount) || 0;
    } else if (item.type === 'expense') {
      acc.expense += parseFloat(item.amount) || 0;
    } else if (item.type === 'asset') {
      acc.asset += parseFloat(item.amount) || 0;
    }
    return acc;
  }, { income: 0, expense: 0, asset: 0 });

  const balance = summary ? (summary.net_balance || 0) : (totals.income - totals.expense);
  const totalPaid = summary?.total_paid || 0;
  const totalUnpaid = summary?.total_unpaid || 0;

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
      </div>
    );
  }

  return (
    <div className="mt-6 px-2">
      {/* Başlık */}
      <div className="mb-8">
        <h1 className="text-2xl font-semibold text-gray-800 dark:text-gray-200">
          Finansal Yönetim
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Gelir, gider ve demirbaşlarınızı takip edin</p>
      </div>

      {/* Özet Kartlar */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {/* Gelir Kartı */}
        <div className="bg-white dark:bg-dark-300 border border-gray-200 dark:border-dark-400 rounded-xl p-5 hover:shadow-md hover:border-red-300 dark:hover:border-red-800 transition-all duration-200">
          <div className="flex items-start justify-between mb-4">
            <div className="p-2.5 bg-red-50 dark:bg-red-900/20 rounded-lg">
              <BsCashStack className="text-xl text-red-600 dark:text-red-400" />
            </div>
            <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">{financials.filter(f => f.type === 'income').length} kayıt</span>
          </div>
          <div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Toplam Gelir</p>
            <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
              ₺{totals.income.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
            </p>
          </div>
        </div>

        {/* Gider Kartı */}
        <div className="bg-white dark:bg-dark-300 border border-gray-200 dark:border-dark-400 rounded-xl p-5 hover:shadow-md hover:border-red-300 dark:hover:border-red-800 transition-all duration-200">
          <div className="flex items-start justify-between mb-4">
            <div className="p-2.5 bg-red-50 dark:bg-red-900/20 rounded-lg">
              <FiDollarSign className="text-xl text-red-600 dark:text-red-400" />
            </div>
            <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">{financials.filter(f => f.type === 'expense').length} kayıt</span>
          </div>
          <div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Toplam Gider</p>
            <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
              ₺{totals.expense.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
            </p>
          </div>
        </div>

        {/* Demirbaş Kartı */}
        <div className="bg-white dark:bg-dark-300 border border-gray-200 dark:border-dark-400 rounded-xl p-5 hover:shadow-md hover:border-red-300 dark:hover:border-red-800 transition-all duration-200">
          <div className="flex items-start justify-between mb-4">
            <div className="p-2.5 bg-red-50 dark:bg-red-900/20 rounded-lg">
              <BsBox className="text-xl text-red-600 dark:text-red-400" />
            </div>
            <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">{financials.filter(f => f.type === 'asset').length} varlık</span>
          </div>
          <div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Demirbaşlar</p>
            <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
              ₺{totals.asset.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
            </p>
          </div>
        </div>

        {/* Net Bakiye Kartı */}
        <div className="bg-white dark:bg-dark-300 border border-gray-200 dark:border-dark-400 rounded-xl p-5 hover:shadow-md hover:border-red-300 dark:hover:border-red-800 transition-all duration-200">
          <div className="flex items-start justify-between mb-4">
            <div className={`p-2.5 rounded-lg ${balance >= 0 ? 'bg-green-50 dark:bg-green-900/20' : 'bg-red-50 dark:bg-red-900/20'}`}>
              <BsBank className={`text-xl ${balance >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`} />
            </div>
            <span className={`text-xs font-medium ${balance >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
              {balance >= 0 ? '↑' : '↓'} {balance >= 0 ? 'Pozitif' : 'Negatif'}
            </span>
          </div>
          <div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Net Bakiye</p>
            <p className={`text-2xl font-semibold ${balance >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
              ₺{balance.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
            </p>
          </div>
        </div>
      </div>

      {/* Ödeme Durumu Özeti */}
      {summary && (totalPaid > 0 || totalUnpaid > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white dark:bg-dark-300 border border-gray-200 dark:border-dark-400 rounded-xl p-4 hover:shadow-md hover:border-red-300 dark:hover:border-red-800 transition-all duration-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-50 dark:bg-red-900/20 rounded-lg">
                <BsCheckCircle className="text-xl text-red-600 dark:text-red-400" />
              </div>
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">Ödendi</p>
                <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  ₺{totalPaid.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-dark-300 border border-gray-200 dark:border-dark-400 rounded-xl p-4 hover:shadow-md hover:border-red-300 dark:hover:border-red-800 transition-all duration-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-50 dark:bg-red-900/20 rounded-lg">
                <BsXCircle className="text-xl text-red-600 dark:text-red-400" />
              </div>
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">Ödenmedi</p>
                <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  ₺{totalUnpaid.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                </p>
              </div>
            </div>
          </div>

          {summary.total_partial > 0 && (
            <div className="bg-white dark:bg-dark-300 border border-gray-200 dark:border-dark-400 rounded-xl p-4 hover:shadow-md hover:border-red-300 dark:hover:border-red-800 transition-all duration-200">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-50 dark:bg-red-900/20 rounded-lg">
                  <BsClock className="text-xl text-red-600 dark:text-red-400" />
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">Kısmi Ödendi</p>
                  <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    ₺{summary.total_partial.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Filtreler ve Ekle Butonu */}
      <div className="bg-white dark:bg-dark-300 border border-gray-200 dark:border-dark-400 rounded-xl p-4 mb-6">
        <div className="flex justify-between items-center flex-wrap gap-4">
          {/* Filtre Butonları */}
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setFilterType('all')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                filterType === 'all'
                  ? 'bg-red-600 dark:bg-red-500 text-white'
                  : 'bg-gray-100 dark:bg-dark-400 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-dark-500'
              }`}
            >
              Tümü ({financials.length})
            </button>
            
            <button
              onClick={() => setFilterType('income')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                filterType === 'income'
                  ? 'bg-red-600 dark:bg-red-500 text-white'
                  : 'bg-gray-100 dark:bg-dark-400 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-dark-500'
              }`}
            >
              Gelir ({financials.filter(f => f.type === 'income').length})
            </button>
            
            <button
              onClick={() => setFilterType('expense')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                filterType === 'expense'
                  ? 'bg-red-600 dark:bg-red-500 text-white'
                  : 'bg-gray-100 dark:bg-dark-400 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-dark-500'
              }`}
            >
              Gider ({financials.filter(f => f.type === 'expense').length})
            </button>
            
            <button
              onClick={() => setFilterType('asset')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                filterType === 'asset'
                  ? 'bg-red-600 dark:bg-red-500 text-white'
                  : 'bg-gray-100 dark:bg-dark-400 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-dark-500'
              }`}
            >
              Demirbaş ({financials.filter(f => f.type === 'asset').length})
            </button>
          </div>

          {/* Aksiyon Butonları */}
          <div className="flex gap-2">
            <Button
              variant="bordered"
              startContent={<FiDownload />}
              onClick={handleExportCSV}
              className="rounded-lg border-gray-300 dark:border-dark-400 hover:border-red-600 dark:hover:border-red-500 hover:text-red-600 dark:hover:text-red-500"
            >
              CSV İndir
            </Button>
            <Button
              className="bg-red-600 dark:bg-red-500 text-white rounded-lg font-medium hover:bg-red-700 dark:hover:bg-red-600"
              startContent={<FiPlus />}
              onClick={() => {
                resetForm();
                setIsModalOpen(true);
              }}
            >
              Yeni Kayıt
            </Button>
          </div>
        </div>
      </div>

      {/* Tablo */}
      <div className="bg-white dark:bg-dark-300 border border-gray-200 dark:border-dark-400 rounded-xl overflow-hidden">
        {filteredFinancials.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-dark-400 border-b border-gray-200 dark:border-dark-500">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase">Tür</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase">Kategori</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase">Açıklama</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase">Tutar</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase">Ödeme</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase">Fatura</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase">Tarih</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-600 dark:text-gray-400 uppercase">İşlem</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-dark-500">
                {filteredFinancials.map((item, index) => (
                  <tr key={index} className="hover:bg-gray-50 dark:hover:bg-dark-400 transition-colors duration-150 group">
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <span className="text-gray-600 dark:text-gray-400">{getTypeIcon(item.type)}</span>
                        <span className="text-sm text-gray-700 dark:text-gray-300">{getTypeLabel(item.type)}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        {getCategoryLabel(item.type, item.category)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="max-w-xs">
                        <p className="text-sm text-gray-900 dark:text-gray-100 truncate">{item.description}</p>
                        {item.notes && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">{item.notes}</p>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                        {parseFloat(item.amount).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} {item.currency || 'TRY'}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className="text-xs text-gray-600 dark:text-gray-400">
                        {getPaymentStatusLabel(item.payment_status)}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className="text-sm text-gray-600 dark:text-gray-400 font-mono text-xs">
                        {item.invoice_number || '-'}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="text-sm">
                        <div className="text-gray-900 dark:text-gray-100">
                          {new Date(item.date).toLocaleDateString('tr-TR')}
                        </div>
                        {item.due_date && (
                          <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                            Vade: {new Date(item.due_date).toLocaleDateString('tr-TR')}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-right">
                      <div className="flex justify-end gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity duration-150">
                        <button
                          onClick={() => handleEdit(item)}
                          className="p-1.5 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors group/btn"
                        >
                          <FiEdit2 className="text-gray-600 dark:text-gray-400 group-hover/btn:text-red-600 dark:group-hover/btn:text-red-400 transition-colors" size={16} />
                        </button>
                        <button
                          onClick={() => handleDelete(item)}
                          className="p-1.5 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors group/btn"
                        >
                          <FiTrash2 className="text-gray-600 dark:text-gray-400 group-hover/btn:text-red-600 dark:group-hover/btn:text-red-400 transition-colors" size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-16">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-50 dark:bg-red-900/20 mb-4">
              <BsBank className="text-2xl text-red-600 dark:text-red-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-2">Henüz kayıt yok</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">İlk finansal kaydınızı oluşturun</p>
            <Button
              className="bg-red-600 dark:bg-red-500 text-white rounded-lg font-medium hover:bg-red-700 dark:hover:bg-red-600"
              startContent={<FiPlus />}
              onClick={() => {
                resetForm();
                setIsModalOpen(true);
              }}
            >
              Kayıt Ekle
            </Button>
          </div>
        )}
      </div>

      {/* Modal */}
      <Modal 
        isOpen={isModalOpen} 
        onClose={() => { setIsModalOpen(false); resetForm(); }} 
        size="2xl"
        scrollBehavior="inside"
        classNames={{
          base: "rounded-xl max-h-[90vh]",
          body: "py-4"
        }}
      >
        <ModalContent className="dark:bg-dark-300">
          <ModalHeader className="border-b border-gray-200 dark:border-dark-400 px-6 py-4">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
              {editingItem ? 'Kaydı Düzenle' : 'Yeni Kayıt Ekle'}
            </h2>
          </ModalHeader>
          <ModalBody className="py-4">
            <div className="space-y-4">
              <div>
                <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-2">Temel Bilgiler</p>
                <div className="space-y-2">
                  <Select
                    label="Tür"
                    placeholder="Tür seçin"
                    selectedKeys={[formData.type]}
                    onChange={(e) => {
                      setFormData({ ...formData, type: e.target.value, category: '' });
                    }}
                  >
                    <SelectItem key="expense" value="expense">Gider</SelectItem>
                    <SelectItem key="income" value="income">Gelir</SelectItem>
                    <SelectItem key="asset" value="asset">Demirbaş</SelectItem>
                  </Select>

                  <Select
                    label="Kategori"
                    placeholder="Kategori seçin"
                    selectedKeys={formData.category ? [formData.category] : []}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  >
                    {categories[formData.type]?.map((cat) => (
                      <SelectItem key={cat.value} value={cat.value}>
                        {cat.label}
                      </SelectItem>
                    ))}
                  </Select>

                  <Input
                    label="Açıklama"
                    placeholder="Örn: Dell Laptop alımı"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  />
                </div>
              </div>

              <div>
                <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-2">Tutar Bilgileri</p>
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    label="Tutar"
                    type="number"
                    placeholder="0.00"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    startContent={<span className="text-gray-400">₺</span>}
                  />

                  <Select
                    label="Para Birimi"
                    selectedKeys={[formData.currency]}
                    onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                  >
                    <SelectItem key="TRY" value="TRY">TRY (₺)</SelectItem>
                    <SelectItem key="USD" value="USD">USD ($)</SelectItem>
                    <SelectItem key="EUR" value="EUR">EUR (€)</SelectItem>
                  </Select>
                </div>
              </div>

              <div>
                <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-2">Tarih Bilgileri</p>
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    label="Tarih"
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  />

                  <Input
                    label="Vade Tarihi (Opsiyonel)"
                    type="date"
                    value={formData.due_date}
                    onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                  />
                </div>
              </div>

              <div>
                <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-2">Ödeme Bilgileri</p>
                <div className="space-y-2">
                  <Select
                    label="Ödeme Durumu"
                    selectedKeys={[formData.payment_status]}
                    onChange={(e) => setFormData({ ...formData, payment_status: e.target.value })}
                  >
                    <SelectItem key="paid" value="paid">Ödendi</SelectItem>
                    <SelectItem key="unpaid" value="unpaid">Ödenmedi</SelectItem>
                    <SelectItem key="partial" value="partial">Kısmi Ödendi</SelectItem>
                  </Select>

                  <Select
                    label="Ödeme Yöntemi (Opsiyonel)"
                    selectedKeys={formData.payment_method ? [formData.payment_method] : []}
                    onChange={(e) => setFormData({ ...formData, payment_method: e.target.value })}
                  >
                    <SelectItem key="cash" value="cash">Nakit</SelectItem>
                    <SelectItem key="bank_transfer" value="bank_transfer">Banka Transferi</SelectItem>
                    <SelectItem key="credit_card" value="credit_card">Kredi Kartı</SelectItem>
                    <SelectItem key="other" value="other">Diğer</SelectItem>
                  </Select>

                  <Input
                    label="Fatura Numarası (Opsiyonel)"
                    placeholder="INV-2025-001"
                    value={formData.invoice_number}
                    onChange={(e) => setFormData({ ...formData, invoice_number: e.target.value })}
                  />
                </div>
              </div>

              <div>
                <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-2">Ek Bilgiler</p>
                <div className="space-y-2">
                  <Textarea
                    label="Not (Opsiyonel)"
                    placeholder="Ek açıklama..."
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    minRows={2}
                  />

                  <div>
                    <label className="text-sm text-gray-700 dark:text-gray-300 mb-2 block">
                      Etiketler (Opsiyonel)
                    </label>
                    <div className="flex gap-2 mb-2">
                      <Input
                        placeholder="Etiket ekle..."
                        value={tagInput}
                        onChange={(e) => setTagInput(e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleAddTag();
                          }
                        }}
                      />
                      <Button 
                        onClick={handleAddTag}
                        className="bg-red-600 dark:bg-red-500 text-white hover:bg-red-700 dark:hover:bg-red-600"
                      >
                        Ekle
                      </Button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {formData.tags.map((tag, idx) => (
                        <Chip
                          key={idx}
                          onClose={() => handleRemoveTag(tag)}
                          variant="flat"
                        >
                          {tag}
                        </Chip>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </ModalBody>
          <ModalFooter className="border-t border-gray-200 dark:border-dark-400 px-6 py-4">
            <Button 
              variant="bordered"
              onClick={() => { setIsModalOpen(false); resetForm(); }}
              className="rounded-lg hover:border-red-600 dark:hover:border-red-500 hover:text-red-600 dark:hover:text-red-500"
            >
              İptal
            </Button>
            <Button 
              onClick={handleSubmit}
              className="bg-red-600 dark:bg-red-500 text-white rounded-lg font-medium hover:bg-red-700 dark:hover:bg-red-600"
            >
              {editingItem ? 'Güncelle' : 'Kaydet'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  );
};

export default FinancialComponent;


